// ═══════════════════════════════════════════════════════════════
// IA Configuration — damage thresholds + parcel data upload
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/gab/GabComponents';
import { IconUpload, IconTrash, IconCheck, IconSettings2 } from '@tabler/icons-react';
import {
  parseCSVHeaders, parseAndStoreParcels, getParcelMeta, clearParcels,
  type FieldMapping, type ParcelMeta,
} from '../../lib/parcelDb';
import { loadIAConfig, saveIAConfig, DEFAULT_THRESHOLDS, type IAThresholds } from '../../lib/iaConfig';

// ── Shared styles ─────────────────────────────────────────────

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: '24px',
  marginBottom: 20,
};

const label: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 4,
  display: 'block',
};

const hint: React.CSSProperties = {
  fontSize: 12,
  color: '#94a3b8',
  marginTop: 2,
};

const numInput: React.CSSProperties = {
  width: 80,
  padding: '6px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 4,
  fontSize: 14,
  textAlign: 'right',
};

const select: React.CSSProperties = {
  padding: '7px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 4,
  fontSize: 13,
  background: '#fff',
  minWidth: 200,
};

// ── Component ─────────────────────────────────────────────────

export default function IAConfigTab() {
  const fileRef = useRef<HTMLInputElement>(null);

  // Thresholds
  const [thresholds, setThresholds]   = useState<IAThresholds>(loadIAConfig());
  const [savedThresh, setSavedThresh] = useState(false);

  // Parcel data
  const [meta,        setMeta]        = useState<ParcelMeta | null>(null);
  const [csvHeaders,  setCsvHeaders]  = useState<string[]>([]);
  const [csvText,     setCsvText]     = useState('');
  const [fieldMap,    setFieldMap]    = useState<Partial<FieldMapping>>({});
  const [progress,    setProgress]    = useState(0);
  const [uploading,   setUploading]   = useState(false);
  const [uploadDone,  setUploadDone]  = useState(false);
  const [error,       setError]       = useState('');

  useEffect(() => {
    getParcelMeta().then(setMeta).catch(() => {});
  }, []);

  // ── Thresholds ────────────────────────────────────────────

  const handleSaveThresholds = () => {
    saveIAConfig(thresholds);
    setSavedThresh(true);
    setTimeout(() => setSavedThresh(false), 2000);
  };

  const setT = (key: keyof IAThresholds, val: string) => {
    const n = parseFloat(val);
    if (!isNaN(n)) setThresholds(prev => ({ ...prev, [key]: n }));
  };

  // ── CSV upload ────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploadDone(false);
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target?.result as string;
      setCsvText(text);
      const headers = parseCSVHeaders(text);
      setCsvHeaders(headers);
      // Auto-detect common column names
      const find = (...candidates: string[]) =>
        headers.find(h => candidates.some(c => h.toLowerCase().includes(c))) ?? '';
      setFieldMap({
        address:       find('address', 'addr', 'street'),
        city:          find('city'),
        assessedValue: find('parval', 'assessedval', 'assessed_val', 'totassess', 'appraisedvalue', 'market'),
        owner:         find('owner', 'ownername'),
      });
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!csvText || !fieldMap.address || !fieldMap.assessedValue) {
      setError('Address and Assessed Value columns are required.');
      return;
    }
    setUploading(true);
    setProgress(0);
    setError('');
    try {
      const count = await parseAndStoreParcels(
        csvText,
        fieldMap as FieldMapping,
        pct => setProgress(pct),
      );
      const newMeta = await getParcelMeta();
      setMeta(newMeta);
      setUploadDone(true);
      setCsvText('');
      setCsvHeaders([]);
      if (fileRef.current) fileRef.current.value = '';
      console.log(`Loaded ${count} parcels`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClear = async () => {
    if (!confirm('Clear all parcel data?')) return;
    await clearParcels();
    setMeta(null);
    setCsvHeaders([]);
    setCsvText('');
    setUploadDone(false);
  };

  const canUpload = csvHeaders.length > 0 && fieldMap.address && fieldMap.assessedValue;

  // ── Render ────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 760 }}>

      {/* ── Damage Category Thresholds ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <IconSettings2 size={18} color="#4B3FFF" />
          <span style={{ fontWeight: 700, fontSize: 15 }}>Damage Category Thresholds</span>
        </div>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
          Define the minimum % of pre-disaster home value at which each damage category applies.
          Assessed damage % = Estimated Loss ÷ Pre-Disaster Value.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 20, marginBottom: 20 }}>
          {/* Affected always starts at 0 */}
          <div>
            <label style={label}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--color-success)', marginRight: 6 }} />
              Affected
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input value="0" disabled style={{ ...numInput, background: '#f8fafc', color: '#94a3b8' }} />
              <span style={{ fontSize: 13, color: '#64748b' }}>%+</span>
            </div>
            <span style={hint}>Always starts at 0%</span>
          </div>

          <div>
            <label style={label}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#d97706', marginRight: 6 }} />
              Minor
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="number" min={1} max={99}
                value={thresholds.minorMin}
                onChange={e => setT('minorMin', e.target.value)}
                style={numInput}
              />
              <span style={{ fontSize: 13, color: '#64748b' }}>%+</span>
            </div>
            <span style={hint}>Default: 15%</span>
          </div>

          <div>
            <label style={label}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#ea580c', marginRight: 6 }} />
              Major
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="number" min={1} max={99}
                value={thresholds.majorMin}
                onChange={e => setT('majorMin', e.target.value)}
                style={numInput}
              />
              <span style={{ fontSize: 13, color: '#64748b' }}>%+</span>
            </div>
            <span style={hint}>Default: 50%</span>
          </div>

          <div>
            <label style={label}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--color-error)', marginRight: 6 }} />
              Destroyed
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="number" min={1} max={100}
                value={thresholds.destroyedMin}
                onChange={e => setT('destroyedMin', e.target.value)}
                style={numInput}
              />
              <span style={{ fontSize: 13, color: '#64748b' }}>%+</span>
            </div>
            <span style={hint}>Default: 80%</span>
          </div>
        </div>

        {/* Visual range summary */}
        <div style={{ background: '#f8fafc', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#64748b', marginBottom: 16 }}>
          Affected: 0–{thresholds.minorMin - 1}% &nbsp;·&nbsp;
          Minor: {thresholds.minorMin}–{thresholds.majorMin - 1}% &nbsp;·&nbsp;
          Major: {thresholds.majorMin}–{thresholds.destroyedMin - 1}% &nbsp;·&nbsp;
          Destroyed: {thresholds.destroyedMin}%+
        </div>

        <Button
          size="sm"
          color="var(--color-primary-700)"
          onClick={handleSaveThresholds}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {savedThresh ? <><IconCheck size={14} /> Saved</> : 'Save Thresholds'}
        </Button>
      </div>

      {/* ── Parcel Data Upload ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <IconUpload size={18} color="#4B3FFF" />
          <span style={{ fontWeight: 700, fontSize: 15 }}>Parcel / Assessed Value Data</span>
        </div>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
          Upload a county assessor CSV to enable pre-disaster home value lookups. Data is stored
          locally in your browser and used to enrich damage assessments at report time.
        </p>

        {/* Current status */}
        {meta && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6,
            padding: '10px 14px', marginBottom: 16,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>
                {meta.count.toLocaleString()} parcels loaded
              </span>
              <span style={{ fontSize: 12, color: '#4ade80', marginLeft: 12 }}>
                Uploaded {new Date(meta.uploadedAt).toLocaleDateString()}
              </span>
            </div>
            <button
              onClick={handleClear}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <IconTrash size={13} /> Clear
            </button>
          </div>
        )}

        {/* File picker */}
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Upload CSV File</label>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            style={{ fontSize: 13 }}
          />
        </div>

        {/* Field mapper — shown once file is parsed */}
        {csvHeaders.length > 0 && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>Map CSV Columns</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

              <div>
                <label style={label}>Address Column <span style={{ color: 'var(--color-error)' }}>*</span></label>
                <select
                  style={select}
                  value={fieldMap.address ?? ''}
                  onChange={e => setFieldMap(prev => ({ ...prev, address: e.target.value }))}
                >
                  <option value="">— select —</option>
                  {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>

              <div>
                <label style={label}>Assessed Value Column <span style={{ color: 'var(--color-error)' }}>*</span></label>
                <select
                  style={select}
                  value={fieldMap.assessedValue ?? ''}
                  onChange={e => setFieldMap(prev => ({ ...prev, assessedValue: e.target.value }))}
                >
                  <option value="">— select —</option>
                  {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>

              <div>
                <label style={label}>City Column <span style={{ color: '#94a3b8' }}>(optional)</span></label>
                <select
                  style={select}
                  value={fieldMap.city ?? ''}
                  onChange={e => setFieldMap(prev => ({ ...prev, city: e.target.value || undefined }))}
                >
                  <option value="">— none —</option>
                  {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>

              <div>
                <label style={label}>Owner Column <span style={{ color: '#94a3b8' }}>(optional)</span></label>
                <select
                  style={select}
                  value={fieldMap.owner ?? ''}
                  onChange={e => setFieldMap(prev => ({ ...prev, owner: e.target.value || undefined }))}
                >
                  <option value="">— none —</option>
                  {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Progress bar */}
        {uploading && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Processing… {progress}%</div>
            <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3 }}>
              <div style={{ height: '100%', width: `${progress}%`, background: '#4B3FFF', borderRadius: 3, transition: 'width 0.3s' }} />
            </div>
          </div>
        )}

        {uploadDone && (
          <div style={{ fontSize: 13, color: '#166534', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconCheck size={14} /> Parcel data loaded successfully.
          </div>
        )}

        {error && (
          <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 12 }}>{error}</div>
        )}

        {canUpload && (
          <Button
            size="sm"
            color="var(--color-primary-700)"
            loading={uploading}
            onClick={handleUpload}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <IconUpload size={14} /> Load Parcel Data
          </Button>
        )}
      </div>
    </div>
  );
}
